import { toCalendarDay } from "../../PagesContent/RecordReplacement/recordReplacementHelper";

// Pure logic for the Edit Pack Details screen (EditPackDetailsPage).
//
// Kept free of React so every rule can be unit-tested directly, the same split
// recordReplacementHelper.js uses for the flow this one mirrors.

// packConfig.userRemarks is TEXT and the service caps it at 1000, so the shared
// shared RemarkField's 250 default would refuse text the backend would happily take. A
// correction's justification is the one place the extra room earns itself.
export const MAX_PACK_REMARK_LENGTH = 1000;

// Index a list of pack-shaped rows by absPackNo. The screen holds `packs`, `forms` and
// `initials` as three arrays that are sorted and filtered independently as the user
// adds and removes cards, so every join between them goes through here rather than
// through a shared array position that only happens to line up.
export const byPack = (rows = []) =>
    (rows || []).reduce((acc, row) => {
        if (row?.absPackNo != null) acc[row.absPackNo] = row;
        return acc;
    }, {});

// Seed one pack's form row from the pack as the components tree returned it.
// installedOn is a UTC datetime on the wire but a calendar day in the picker, so it is
// held locally as the user's day and converted back at submit.
export const buildInitialPackForm = (pack, toLocalDay) => ({
    absPackNo: pack?.absPackNo,
    // Trimmed on seed: this DB carries trailing spaces in config columns, and the
    // change check compares a trimmed form value against this. Untrimmed, a pack with
    // " ABC123" on record opened already marked Changed, with a "was ABC123" hint
    // under a field that looked identical and Confirm live before the user typed.
    customerPackID: (pack?.serial || "").trim(),
    // Through toCalendarDay, the same trim the registry auto-fill applies. The
    // components tree can return "2023-05-01T00:00:00" where the registry returns
    // "2023-05-01"; untrimmed, auto-filling an already-correct date would read as a
    // change and write a no-op audit row.
    manufacturingDate: toCalendarDay(pack?.manufacturingDate),
    installedOn: pack?.installedOn ? toLocalDay(pack.installedOn) : null,
});

// Whether anything the user can change actually differs from what the screen opened
// opened with. Exported so the "nothing has changed" rule is testable without rendering.
export const hasPackDetailsChanged = (form, initial) =>
    (form?.customerPackID || "").trim() !== (initial?.customerPackID || "") ||
    (form?.manufacturingDate || null) !== (initial?.manufacturingDate || null) ||
    (form?.installedOn || null) !== (initial?.installedOn || null);

// Per-pack field rules. The backend enforces all of these again -- these exist so the
// user is told which field of which pack is wrong instead of reading a generic toast.
export const validatePackRow = (form) => {
    const errs = {};
    const serial = (form?.customerPackID || "").trim();
    if (!serial) {
        errs.customerPackID = "Enter the pack's serial number";
    } else if (serial.length > 255) {
        errs.customerPackID = "Serial number is too long (255 characters maximum)";
    }
    if (
        form?.manufacturingDate &&
        form?.installedOn &&
        form.installedOn < form.manufacturingDate
    ) {
        errs.installedOn = "A pack cannot be installed before it was manufactured";
    }
    return errs;
};

// Whole-form rules across every selected pack.
//
// Returns { rows: {absPackNo: {field: msg}}, remark, remarkCount, submit } so each
// pack's section can highlight its own fields while the shared remark and the
// cross-pack rules report once at the bottom.
// `packs` is optional and used only for wording: a message that says "String 4 · Pack 8"
// matches the card heading the user is looking at, where a bare "Pack 8" makes them
// hunt for it.
export const labelForSlot = (packs, absPackNo) => {
    const pack = (packs || []).find((p) => p?.absPackNo === absPackNo);
    const label = [pack?.stringLabel, pack?.label].filter(Boolean).join(" · ");
    return label || `Pack ${absPackNo}`;
};

export const validatePackDetailsForm = ({
    forms,
    initials,
    remark,
    packs,
    batteryID,
}) => {
    const rows = {};
    let hasRowError = false;
    // Joined on absPackNo rather than by position -- `forms` and `initials` are built
    // and re-sorted separately as the user adds and removes packs, so an index join
    // would compare one pack's entry against another pack's starting values. Read by
    // the anyChanged test at the bottom.
    const initialByPack = byPack(initials);
    // Every slot this request covers. A serial held by one of them is not a clash --
    // the request frees it in the same transaction.
    const editedAbsPackNos = (packs || []).map((pack) => pack?.absPackNo);
    (forms || []).forEach((form) => {
        const rowErrs = validatePackRow(form);
        // Derived HERE, from the registry row the lookup stored, rather than frozen on
        // the form when that lookup ran. editedAbsPackNos changes every time the user
        // adds or removes a pack, and a frozen verdict cannot follow it: the conflict
        // message tells the user to "correct that pack in the same request", and once
        // they did, a stale verdict kept Confirm blocked with no way out but retyping.
        // The inverse was worse and silent -- removing a pack left a cleared conflict
        // behind and let a doomed request through to a 409.
        const registry = form?.serialRegistry;
        const serial = (form?.customerPackID || "").trim();
        if (!rowErrs.customerPackID && registry && registry.term === serial) {
            const conflict = getSerialSlotConflict(registry.row, {
                batteryID,
                editedAbsPackNos,
            });
            if (conflict) rowErrs.customerPackID = formatSerialSlotConflict(conflict);
        }
        if (Object.keys(rowErrs).length) {
            rows[form.absPackNo] = rowErrs;
            hasRowError = true;
        }
    });

    const errs = { rows };
    if (!(remark || "").trim()) errs.remark = true;
    if ((remark || "").length > MAX_PACK_REMARK_LENGTH) {
        // Reported ON the field, not as a page-level summary: the field is where the
        // user has to act, and it is the thing the page scrolls to and focuses.
        errs.remarkCount = true;
        errs.remark =
            `This is too long. Shorten it to ${MAX_PACK_REMARK_LENGTH} characters ` +
            "or fewer.";
    }

    // Two packs cannot be given the same serial: the backend rejects it, and it would
    // otherwise install one serial into two slots.
    //
    // Reported on BOTH offending fields as well as at the bottom. A summary alone
    // makes the user hunt for which two cards it means -- and with the cards scrolled
    // apart, the message can be off screen from either of them.
    //
    // Keyed case-insensitively to match the backend, which folds case because the
    // column collation decides.
    const bySerial = {};
    (forms || []).forEach((form) => {
        const serial = (form?.customerPackID || "").trim();
        if (!serial) return;
        const key = serial.toLowerCase();
        const firstSlot = bySerial[key];
        if (firstSlot !== undefined) {
            const firstLabel = labelForSlot(packs, firstSlot);
            const thisLabel = labelForSlot(packs, form.absPackNo);
            rows[form.absPackNo] = {
                ...(rows[form.absPackNo] || {}),
                customerPackID: `Already entered for ${firstLabel}`,
            };
            // Only names the FIRST clash on the original card. With three cards
            // sharing a serial the later pairs would otherwise overwrite this and the
            // card would point at just one of its two duplicates.
            if (!rows[firstSlot]?.customerPackID) {
                rows[firstSlot] = {
                    ...(rows[firstSlot] || {}),
                    customerPackID: `Already entered for ${thisLabel}`,
                };
            }
            hasRowError = true;
        } else {
            bySerial[key] = form.absPackNo;
        }
    });

    // Judged across the whole selection, so editing one pack of four is valid.
    const anyChanged = (forms || []).some((form) =>
        hasPackDetailsChanged(form, initialByPack[form?.absPackNo])
    );
    // The ONLY page-level message left. It is the one objection that points at no
    // field -- nothing is wrong anywhere, there is just nothing to send -- so there is
    // nothing to scroll to or focus. Everything else reports on the field it is about.
    if (!anyChanged) {
        errs.submit =
            "Nothing to save yet. Change a serial number or a date on at least one " +
            "pack before confirming.";
    }

    errs.hasRowError = hasRowError;
    return errs;
};

export const hasBlockingErrors = (errs) =>
    !!(errs?.hasRowError || errs?.remark || errs?.remarkCount || errs?.submit);

// The fields of one pack card, in the order they are read down the card.
const PACK_FIELD_ORDER = ["customerPackID", "manufacturingDate", "installedOn"];

// Which field a failed submit should take the user to, as a key the page resolves to a
// DOM node. Null when nothing is wrong or when the only objection points at no field.
//
// The order below is SCREEN order -- pack cards down the page in slot order, each
// card's fields top to bottom, then the remark underneath them -- so the user always
// lands on the FIRST thing that needs attention. Getting this wrong sends someone past
// the problem to a later one and leaves them scrolling back. Pure so the ordering is
// unit-tested rather than eyeballed on a page taller than the screen.
export const firstErrorField = (errs, packs = []) => {
    const rows = errs?.rows || {};
    for (const pack of packs || []) {
        const rowErrs = rows[pack?.absPackNo];
        if (!rowErrs) continue;
        for (const field of PACK_FIELD_ORDER) {
            if (rowErrs[field]) return { absPackNo: pack.absPackNo, field };
        }
    }
    // Below every card, so it is only the target once the cards are clean.
    if (errs?.remark || errs?.remarkCount) return { field: "remark" };
    return null;
};

// Whether a serial the registry already has is sitting in a pack this request will not
// free, given the row the list endpoint returned for it.
//
// Mirrors the backend's rule exactly, including its exception: EVERY slot in the request
// is excluded, not just the one being edited. Two slots onboarded with their serials
// transposed is precisely what this screen exists to correct, and swapping them is a
// single valid request -- but each serial is currently held by the OTHER slot, so
// excluding only self would flag both and make the fix look impossible.
//
// Returns null when there is no clash, otherwise { batteryID, absPackNo, vehicleLabel }.
// vehicleLabel is null for a bus the caller cannot see -- the list endpoint is
// scope-filtered -- so the message has to read without it.
export const getSerialSlotConflict = (
    registryRow,
    { batteryID, editedAbsPackNos = [] } = {}
) => {
    const row = registryRow || {};
    const ownerBatteryID = row.batteryID;
    // Not installed anywhere, or the registry knows the serial but nothing holds it.
    if (!ownerBatteryID) return null;

    const sameBattery = String(ownerBatteryID) === String(batteryID);
    const ownerAbsPackNo = row.packNo;
    const edited = new Set((editedAbsPackNos || []).map((n) => String(n)));
    if (sameBattery && ownerAbsPackNo != null && edited.has(String(ownerAbsPackNo))) {
        return null;
    }

    return {
        batteryID: ownerBatteryID,
        absPackNo: ownerAbsPackNo ?? null,
        vehicleLabel: row.licensePlateNo || row.vin || null,
        sameBattery,
    };
};

// The message for that clash. Says WHERE the serial is when the caller can see it, and
// stays honest when they cannot rather than implying the serial is free.
export const formatSerialSlotConflict = (conflict) => {
    if (!conflict) return null;
    const { sameBattery, absPackNo, vehicleLabel } = conflict;
    if (sameBattery) {
        return absPackNo != null
            ? `This serial is already on Pack ${absPackNo} of this vehicle. ` +
              "Correct that pack in the same request, or use a different serial."
            : "This serial is already on another pack of this vehicle.";
    }
    return vehicleLabel
        ? `This serial is installed on ${vehicleLabel}. Remove it there first, or ` +
          "use a different serial."
        : "This serial is installed on another vehicle. Remove it there first, or " +
          "use a different serial.";
};

// Turn the backend's fieldErrors block into the same { rows } shape validation
// produces, so a server conflict highlights, scrolls to and focuses its field exactly
// like a local one. Entries for packs no longer on the page are dropped -- a pointer at
// a card that is not rendered would leave the user with a highlighted nothing and the
// summary as their only clue.
//
// `fallback` is the envelope message, used when the response carries no pointer at all
// (an older backend, or a conflict that genuinely is not about one field).
export const applyServerFieldErrors = (fieldErrors, packs = [], fallback = "") => {
    const known = new Set((packs || []).map((pack) => String(pack?.absPackNo)));
    const rows = {};
    (fieldErrors || []).forEach((entry) => {
        const absPackNo = entry?.absPackNo;
        const field = entry?.field;
        if (absPackNo == null || !field) return;
        if (!known.has(String(absPackNo))) return;
        rows[absPackNo] = {
            ...(rows[absPackNo] || {}),
            [field]: entry.message || fallback,
        };
    });
    const hasRowError = Object.keys(rows).length > 0;
    // The banner is suppressed once a field carries the message: showing both says the
    // same thing twice, in the two places furthest apart on the page.
    return hasRowError
        ? { rows, hasRowError: true }
        : { rows: {}, submit: fallback };
};

// The page keys one flat ref map by this, so a pack field and the remark share one
// lookup and one scroll mechanism.
export const fieldRefKey = (target) =>
    target?.absPackNo == null
        ? target?.field || null
        : `${target.absPackNo}:${target.field}`;

// One pack's entry in the request body.
//
// `expected` is built from PACK -- the values the screen opened with -- never from
// `form`. Building it from current state would make it echo whatever the user just
// typed, so the backend's comparison would always pass and the 409 guard would be
// silently dead. Every key is always sent, so a field this user never touched but
// someone else changed still conflicts.
export const buildPackDetailsPayload = ({ pack, form, initial, toUTC }) => ({
    absPackNo: pack?.absPackNo,
    customerPackID: (form?.customerPackID || "").trim(),
    manufacturingDate: form?.manufacturingDate || null,
    // installedOn is a UTC DATETIME on the wire but only a calendar DAY in the picker,
    // so the round trip is lossy: converting an untouched value would replace the
    // technician-entered work-end instant the replacement flow recorded (say 06:30)
    // with local midnight. The displayed day would not change, so nothing would look
    // wrong -- the time would just be silently gone, and not recoverable from the audit
    // log as a user-intended edit. Only convert a day the user actually picked.
    installedOn:
        (form?.installedOn || null) === (initial?.installedOn || null)
            ? pack?.installedOn || null
            : form?.installedOn
            ? toUTC(form.installedOn)
            : null,
    expected: {
        customerPackID: pack?.serial || null,
        // Through toCalendarDay, the same trim the registry auto-fill applies. The
    // components tree can return "2023-05-01T00:00:00" where the registry returns
    // "2023-05-01"; untrimmed, auto-filling an already-correct date would read as a
    // change and write a no-op audit row.
    manufacturingDate: toCalendarDay(pack?.manufacturingDate),
        installedOn: pack?.installedOn || null,
    },
});

// The full request body: one entry per selected pack, plus ONE shared remark. Mirrors
// the replacement flow, where a whole selection is submitted under a single remark
// because it was one deliberate act.
export const buildBulkPackDetailsPayload = ({ packs, forms, initials, remark, toUTC }) => {
    // Same identity join as validatePackDetailsForm. Getting this wrong would send one
    // pack's corrected serial to another pack's slot, which no validation downstream
    // could catch -- both entries are individually well-formed.
    const formByPack = byPack(forms);
    const initialByPack = byPack(initials);
    return {
        userRemarks: (remark || "").trim(),
        packs: (packs || []).map((pack) =>
            buildPackDetailsPayload({
                pack,
                form: formByPack[pack?.absPackNo],
                initial: initialByPack[pack?.absPackNo],
                toUTC,
            })
        ),
    };
};


// Packs in this selection that have a recorded replacement behind them.
//
// Editing one is legitimate -- a serial mistyped while RECORDING a replacement is
// exactly the thing this feature fixes -- but it is the case where a correction can
// disagree with history, so the user is told rather than left to find out. The
// replacement log keeps the serials frozen as they were recorded, by design, so a
// corrected pack will read one way on its card and another way in Replacement Logs.
export const getReplacedPacks = (packs = []) =>
    (packs || []).filter((pack) => pack?.hasReplacementHistory);

// The warning shown when any selected pack has been replaced. Null when none have,
// so the caller renders nothing rather than an empty banner.
export const buildReplacementWarning = (packs = [], formatDate = (v) => v) => {
    const replaced = getReplacedPacks(packs);
    if (!replaced.length) return null;

    return {
        count: replaced.length,
        // Dated where we have it: "was this the replacement I just recorded?" is the
        // question a user actually needs answered before correcting. `name` here is the
        // only place the pack label is built -- a separate `names` list used to sit
        // alongside it recomputing the same strings for nobody.
        dates: replaced.map((pack) => ({
            name: [pack?.stringLabel, pack?.label].filter(Boolean).join(" · "),
            lastReplacedAt: pack?.lastReplacedAt
                ? formatDate(pack.lastReplacedAt)
                : null,
        })),
    };
};
